import { LlmUsageFilters } from 'src/app/core/services/llm-usage-api.service';

export interface DateRangePreset {
  key: string;
  label: string;
  days: number;
}

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
];

/** ISO range for a preset. Dates go out in UTC because usage timestamps are recorded in UTC. */
export function rangeFor(days: number, now = new Date()): { from: string; to: string } {
  const to = new Date(now);
  const from = new Date(now.getTime() - days * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Drop empty values so a blank filter box does not narrow the query to nothing. */
export function cleanFilters(filters: LlmUsageFilters): LlmUsageFilters {
  const out: LlmUsageFilters = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}

/**
 * Compact large token counts for display only (12,500,000 → "12.5M").
 *
 * Never used for money: a rounded cost is a wrong cost, and these screens exist to be
 * reconciled against a provider invoice.
 */
export function compactTokens(value: number | null | undefined): string {
  const count = value ?? 0;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(count >= 10_000_000 ? 0 : 1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(count >= 10_000 ? 0 : 1)}K`;
  return String(count);
}

/**
 * Format a decimal-string USD cost for display, keeping enough places that a single call
 * does not read as $0.00.
 */
export function formatUsd(value: string | null | undefined): string {
  const amount = Number(value ?? 0);
  if (amount === 0) return '$0.00';
  if (amount < 0.01) return `$${amount.toFixed(6)}`;
  if (amount < 1) return `$${amount.toFixed(4)}`;
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
